import { useState, useEffect } from "react";

import {
    ApplePaySupportedMerchantCapabilities,
    ApplePaySupportedNetworks,
    CountryCodes,
    DigitalWalletProviders,
    PaymentMethodType,
    TransactionStatus,
    Currency } from "../../common/constants";
import ApiClient from "../../ApiClient";

const apiClient = ApiClient();

const usePaymentLink = (profileName, orderRef, merchantId) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isFetchingPaymentDetails, setIsFetchingPaymentDetails] = useState(true);
    const [isFetchingMerchantLogo, setIsFetchingMerchantLogo] = useState(true);
    const [isPaying, setIsPaying] = useState(false);
    const [paymentResponseDto, setPaymentResponseDto] = useState({});
    const [paymentLinkDetails, setPaymentLinkDetails] = useState(null);
    const [merchantLogo, setMerchantLogo] = useState(null);
    const [error, setError] = useState(null);
    const [amount, setAmount] = useState();
    const [products, setProducts] = useState(null);
    const [failUrl, setFailUrl] = useState('');
    const [showAuthView, setShowAuthView] = useState(false);
    const [authViewHtml, setAuthViewHtml] = useState('');
    const [authViewUrl, setAuthViewUrl] = useState('');

    const { 
        pay,
        applePayTransactionStartSession,
        payV2,
        getLiveLinkDetails,
        getMerchantLogo,
        initLiveLinkPayment,
        updateTransactionMetaData,
        getAuthenticationView,
        getAuthenticationStatus } = apiClient;

    useEffect(() => {
        if (products === null) {
            return;
        }

        const getAmount = () => {
            return products.reduce((total, item) => total + item.price, 0);
        }

        setAmount(getAmount());
    }, [products]);

    useEffect(() => {
        if (!orderRef) {
            setIsFetchingPaymentDetails(false);
            return;
        }

        const onGetLiveLinkDetailsSuccess = (response) => {
            if (response.succeeded === true) {
                setProducts(response.response.products);
                setPaymentLinkDetails(response.response);
            }
            else {
                onFail(response.error_message);
            }
        }

        const onFail = (errorMsg) => {
            setError(errorMsg ?? "Couldn't fetch payment details");
            setIsFetchingPaymentDetails(false);
        }

        const fetchDetails = async () => {
            try {
                setIsFetchingPaymentDetails(true); 
                await getLiveLinkDetails(profileName, orderRef, onGetLiveLinkDetailsSuccess, onFail);
            } catch (err) {
                setError(err);
            } finally {
                setIsFetchingPaymentDetails(false); 
            }
        };

        fetchDetails();
    }, [orderRef, profileName, getLiveLinkDetails]);

    useEffect(() => {
        if (!merchantId) {
            setIsFetchingMerchantLogo(false);
            return;
        }

        const onGetMerchantLogoSuccess = (response) => {
            if (response.succeeded === true) {
                setMerchantLogo(response.response.merchant_logo);
            } else {
                onFail();
            }
        }

        const onFail = () => {
            setIsFetchingMerchantLogo(false)
        }

        const fetchLogo = async () => {
            try {
                setIsFetchingMerchantLogo(true);
                await getMerchantLogo(merchantId, onGetMerchantLogoSuccess, onFail);
            } catch (err) {
                console.log(err);
            } finally {
                setIsFetchingMerchantLogo(false);
            }
        }

        fetchLogo();
    }, [merchantId, getMerchantLogo]);

    useEffect(() => {
        setIsLoading(isFetchingPaymentDetails || isFetchingMerchantLogo);
    }, [isFetchingPaymentDetails, isFetchingMerchantLogo]);

    const getExpirationMonth = (date) => {
        return +date.substring(0, 2);
    }

    const getExpirationYear = (date) => {
        const year = Math.floor(new Date().getFullYear() / 100) * 100;
        return year + (+date.substring(3, 5));
    }

    const initiatePayment = async (selectedPaymentMethod) => {
        if (!profileName || !orderRef) return;

        var authToken = null;
        var sessionId = null;

        const getTokenFromFullUrl = (url) => {
            const parsedUrl = new URL(url);
            return parsedUrl.searchParams.get("token");
        };

        const onSuccess = (response) => {
            if (response.succeeded === true) {
                authToken = getTokenFromFullUrl(response.response.checkout_url);
                sessionId = response.response.session_id;
                setFailUrl(response.response.failure_url);
            } else {
                onFail(response);
            }
        }

        const onFail = (response) => {
            console.log("fail: ", response);
            setError(response.error_message);
            setIsPaying(false);
        }

        try {
            setIsPaying(true);
            await initLiveLinkPayment(profileName, {'payment_method_type': selectedPaymentMethod}, orderRef, onSuccess, onFail);
        } catch (e) {
            setError(e);
        } finally {
            setIsPaying(false);
            return { authToken, sessionId } ;
        }
    };

    const handleApplePayPayment = (merchantName, paymentSession) => {

        setIsPaying(true);
        const paymentRequest = {
            countryCode: CountryCodes.EGYPT,
            currencyCode: Currency.EGP,
            supportedNetworks: ApplePaySupportedNetworks,
            merchantCapabilities: ApplePaySupportedMerchantCapabilities,
            total: {
                label: merchantName,
                amount: amount.toFixed(2)
            },
        };

        const session = new window.ApplePaySession(3, paymentRequest);

        const onMerchantValidationSuccess = (response) => {
            if (Object.hasOwn(response, "succeeded") && response.succeeded && response.response?.session) {
                return session.completeMerchantValidation(response.response?.session);
            }
            alert(response.error_message || "Apple pay merchant validation error")
            session.abort();
        }

        const onMerchantValidationFailure = (error) => {
            alert(error.error_message || "Apple pay merchant validation error")
            session.abort();
        }

        session.onvalidatemerchant = async (event) => {
            const requestDTO = {
                session_url: event.validationURL
            }
            await applePayTransactionStartSession(
                requestDTO, paymentSession.authToken, onMerchantValidationSuccess, onMerchantValidationFailure);
        };
        const onSuccess = (responseDto) => {
            setPaymentResponseDto(responseDto);
            if (responseDto?.succeeded) {
                if (!responseDto.error_message && responseDto.transaction_status === TransactionStatus.Successful) {
                    window.location.replace(`${responseDto.redirection_url}?${new URLSearchParams(responseDto).toString()}`);
                } else {
                    const errorMessage = responseDto.error_message ?? "Payment failed";
                    window.location.replace(`${responseDto.redirection_url}?error_code=${responseDto.error_code}&error_message=${errorMessage}`);
                }
            } else if (responseDto?.succeeded === false) {
                setError(responseDto.error_message);
            } else {
                setError("Something went wrong");
            }
        }

        const onFail = (response) => {
            console.log("fail: ", response);
        }

        const onPaymentChargeSuccess = (response) => {
            if (response.succeeded && !response.error_message) {
                session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
                onSuccess(response)
            } else {
                session.completePayment(window.ApplePaySession.STATUS_FAILURE);
                onFail({ message: response.error_message, ...response })
            }
        }

        const onPaymentChargeFail = (error) => {
            session.completePayment(window.ApplePaySession.STATUS_FAILURE);
            console.log(error);
            alert(error.message);
        }

        session.onpaymentauthorized = async (event) => {
            try {
                const payload = {
                    session_id: paymentSession.sessionId,
                    payment_token: JSON.stringify(event.payment.token.paymentData),
                    digital_wallet_provider: DigitalWalletProviders.ApplePay
                }
                await payV2(payload, paymentSession.authToken, onPaymentChargeSuccess, onPaymentChargeFail);
            } catch (err) {
                console.log("Error Payment", err);
                session.completePayment(window.ApplePaySession.STATUS_FAILURE);
            }
        };

        session.oncancel = event => {
            setIsPaying(false);
        };

        session.begin();
    };

    const updateMetadata = async (authToken, metadata) => {
        const onSuccess = (response) => {
            if (response.succeeded !== true) {
                onFail(response.error_message);
            }
            setIsPaying(false);
        };

        const onFail = (errorMsg) => {
            setError(errorMsg ?? "Couldn't process your request please try again later");
            setIsPaying(false);
        };

        try {
            setIsPaying(true);
            await updateTransactionMetaData(authToken, metadata, onSuccess, onFail);
            return true;
        } catch (err) {
            onFail(err);
            return false;
        } finally {
            setIsPaying(false);
        }
    }

    const handlePayment = async (formData, paymentSession, selectedPaymentMethod) => {
        const getPaymentTransactionRequestDto = (formData) => {
            if (selectedPaymentMethod === PaymentMethodType.Wallet) {
                return {
                        "payment_number": formData.payment_number.replace(/ /g, ''),
                        "session_id": paymentSession.sessionId,
                    };
            }

            return {
                "payment_number": formData.payment_number.replace(/ /g, ''),
                "expiration_month": getExpirationMonth(formData.expiry_date),
                "expiration_year": getExpirationYear(formData.expiry_date),
                "first_name": formData.first_name,
                "last_name": formData.last_name,
                "security_code": formData.cvv,
                "should_save_payment_method": formData.should_save_payment_method,
                "session_id": paymentSession.sessionId,
            }
        }

        const onSuccess = (responseDto) => {
            setPaymentResponseDto(responseDto);
            if (responseDto?.succeeded) {
                if (!responseDto.error_message && responseDto.transaction_status === TransactionStatus.Successful) {
                    window.location.replace(`${responseDto.redirection_url}?${new URLSearchParams(responseDto).toString()}`);
                } else {
                    const errorMessage = responseDto.error_message ?? "Payment failed";
                    window.location.replace(
                        `${responseDto.redirection_url}?error_code=${responseDto.error_code}&error_message=${errorMessage}&session_id=${paymentSession.sessionId}`);
                }
            } else if (responseDto?.succeeded === false) {
                setError(responseDto.error_message);
            } else {
                setError("Something went wrong");
            }
        }

        const onFail = (response) => {
            console.log("fail: ", response);
        }

        try {
            setIsPaying(true);
            await pay(getPaymentTransactionRequestDto(formData), paymentSession.authToken, onSuccess, onFail);
        } catch (err) {
            setError(err);
        } finally {
            setIsPaying(false);
        }
    }

    const handleAuthentication = async (formData, paymentSession, selectedPaymentMethod) => {
        const getAuthenticationViewRequestDto = (formData) => {
            if (selectedPaymentMethod === PaymentMethodType.Wallet) {
                return {
                    "payment_number": formData.payment_number.replace(/ /g, ''),
                    "session_id": paymentSession.sessionId,
                };
            }

            return {
                "payment_number": formData.payment_number.replace(/ /g, ''),
                "expiration_month": getExpirationMonth(formData.expiry_date),
                "expiration_year": getExpirationYear(formData.expiry_date),
                "first_name": formData.first_name,
                "last_name": formData.last_name,
                "session_id": paymentSession.sessionId,
                "cvv": formData.cvv,
            };
        }

        const onSuccess = (responseDto) => {
            if (responseDto?.succeeded) {
                setShowAuthView(true);
                setAuthViewHtml(responseDto.otpHtmlContent);
                setAuthViewUrl(responseDto.otpUrl);

                const intervalId = setInterval(async () => {
                    await getAuthenticationStatus(paymentSession.authToken, paymentSession.sessionId,
                        (statusDto) => {
                            if (statusDto?.succeeded) {
                                if (statusDto.status === "InProgress") {
                                    return;
                                } else if (statusDto.status === "Successful") {
                                    clearInterval(intervalId);
                                    setShowAuthView(false);
                                    handlePayment(formData, paymentSession, selectedPaymentMethod);
                                } else {
                                    clearInterval(intervalId);
                                    setShowAuthView(false);
                                    window.location.replace(`${failUrl}?error_message=DO_NOT_PROCEED`);
                                }
                            } else if (statusDto?.succeeded === false) {
                                clearInterval(intervalId);
                                setShowAuthView(false);
                                setError(statusDto.error_message);
                            } else {
                                clearInterval(intervalId);
                                setShowAuthView(false);
                                window.location.replace(`${failUrl}?error_code=${statusDto.error_code}&error_message=${statusDto.error_message}`);
                            }
                        },
                        (error) => console.log(error)
                    );
                }, 5000);

            } else if (responseDto?.succeeded === false) {
                setError(responseDto.error_message);
            } else {
                setError("Something went wrong");
            }
        }

        const onFail = (response) => {
            console.log("fail: ", response);
        }

        try {
            await getAuthenticationView(
                getAuthenticationViewRequestDto(formData), paymentSession.authToken, onSuccess, onFail);
        } catch (err) {
            setError(err);
        } finally {
            setIsPaying(false);
        }
    }

    const submitPayment = async (paymentSession, formData, selectedPaymentMethod, merchantName) => {
        if (selectedPaymentMethod === PaymentMethodType.ApplePay) {
            return handleApplePayPayment(merchantName, paymentSession);
        }

        if (paymentLinkDetails.merchant_config.is_3ds_required || selectedPaymentMethod === PaymentMethodType.Wallet) {
            await handleAuthentication(formData, paymentSession, selectedPaymentMethod);
        }
        else {
            await handlePayment(formData, paymentSession, selectedPaymentMethod);
        }
    };

    return {
        isLoading,
        error,
        paymentLinkDetails,
        merchantLogo,
        paymentResponseDto,
        isPaying,
        amount,
        initiatePayment,
        submitPayment,
        updateMetadata,
        showAuthView,
        authViewHtml,
        authViewUrl };
};

export default usePaymentLink;
